import {Component} from '@angular/core';
import {FilterChangedEvent, GridApi, SelectionColumnDef, ViewportChangedEvent} from "@ag-grid-community/core";
import {pageContext, tableColumns} from "@pages/system/client-portal/dashboard-activity/dashboard-activity.component.ds"
import {TableBaseComponent} from "@pages/table.base.component";
import {KpiSidebarItem} from "@shared/ag-kpi-sidebar/ag-kpi-sidebar.component.ds";
import {ActivatedRoute} from "@angular/router";
import {ActivityLineChartData, DashboardActivity} from "@data/interefaces/client-portal.interfaces";
import { TimeUnit, YesNoValue} from "@data/enums/data.enums"
import {AgGridService} from "@shared/services/ag-grid.service";
import {FeatureComponentMode} from "@core/enums/core.enums";
import {DashboardActivityService} from "@data/client-portal/dashboard-activity.service";
import {ClientPortalActivityService} from "@data/client-portal/client-portal-activity.service";

@Component({
  selector: 'app-dashboard-activity',
  templateUrl: './dashboard-activity.component.html',
  styleUrls: ['./dashboard-activity.component.scss'],
  providers: [AgGridService]
})

export class DashboardActivityComponent extends TableBaseComponent {
  currentMode: string = FeatureComponentMode.DATA;
  scale: TimeUnit = TimeUnit.HOUR;

  refreshComplete: boolean = true;
  isFilterActive: boolean = false;
  rowData: DashboardActivity[] = [];
  kpiSidebar: KpiSidebarItem[] = [];

  chartOptions: any;
  dataTypeOptions: any;
  chartTitle = `Dashboard Activity Charts`;
  selectedDataType: string = 'newSessions';
  page:string = 'dashboardActivity';

  protected override readonly tableColumns = tableColumns;
  protected readonly pageContext = pageContext;
  private readonly pageContextNonTableFilterParameters=['mode','scale'];

  constructor(private _activatedRoute: ActivatedRoute, private _dashboardActivityService: DashboardActivityService, private _clientPortalActivityService:ClientPortalActivityService) {
    super();
  }

  public override ngOnInit() {
    super.ngOnInit();
    this.initiatePageState(this._activatedRoute.snapshot.queryParamMap);
    if(this.pageContext.mode) this.switchMode(this.pageContext.mode);
    if(this.pageContext.scale) this.initiateScaleValue(this.pageContext.scale);
    this.dataTypeOptions = this._clientPortalActivityService.getChartDataTypesFor(this.page);
    this.refreshView();
  }

  switchMode(mode: string): void {
    this.currentMode = mode;
    this.pageContext.mode = mode;
    this.updateToPageContextUrl();
    if (!this.isDataMode()) {
      this.loadChartData();
    }
  }

  initiateScaleValue(scaleStr:string){
    let scaleUnit = Object.values(TimeUnit)
      .filter(value => value != TimeUnit.SECOND && value.toString() == scaleStr)[0];
    if(scaleUnit) this.onScaleChanged(scaleUnit);
  }

  isDataMode(): boolean {
    return this.currentMode.toLowerCase() === 'data';
  }

  private loadChartData() {
    if (this.isGridGrouped()) {
      this.loadChartDataWithGroup();
    } else {
      this.loadChartDataWithoutGroup();
    }
  }

  refreshView() {
    this.refreshComplete = false;
    this.displayProgressBar(true);
    this._dashboardActivityService.getAllDashboardActivity()
      .then(result => {
        this.rowData = this.convertResults(result);
      })
      .catch(error => this.displayNotificationMessage('error', JSON.stringify(error)))
      .finally(() => {
        this.refreshComplete = true;
        this.displayProgressBar(false)
      });
  }

  onFilterChanged(event: FilterChangedEvent<any>) {
    const updateLiveDataFilter = this.isUpdateLiveDataFilterRequired(event.columns);
    this._agGridService.processOnFilterChanged(event, this.pageContext, this.updatePageContext.bind(this)).then(() => {
      if (updateLiveDataFilter) {
        this.onLiveDataFilterChange(true);
      }
    });
  }

  onLiveDataFilterChange(forceReload: boolean = false): void {
    this.updateToPageContextUrl();
  }

  onScaleChanged(selectedValue: TimeUnit): void {
    this.scale = selectedValue;
    this.pageContext.scale = selectedValue.toString();
    this.updateToPageContextUrl();
    this.loadComponentsData();
  }

  onDataTypeChange(selectedValue: string): void {
    this.selectedDataType = selectedValue;
    this.loadChartData()
  }

  protected override updateAgGridFilters(pageContext: any, api: GridApi) {
    this._agGridService.updateAgGridFiltersHandler(pageContext, api, this.pageContextNonTableFilterParameters);
  }

  private getFilteredData(): DashboardActivity[] {
    return this.getRowsAfterFilter().filter(row => !!row);
  }

  protected override getSelectionColumnDef(): SelectionColumnDef {
    return {};
  }

  private loadComponentsData() {
    this.loadKpiMetrics();
    if (!this.isDataMode()) {
      this.loadChartData();
    }
  }

  private loadKpiMetrics() {
    this.kpiSidebar = this._dashboardActivityService.getDashboardActivityKpi(this.getFilteredData(), this.scale);
  }

  protected override updatePageContext(updateContextUrl: boolean = true): void {
    this.loadComponentsData();
  }

  protected override onViewportChanged(event: ViewportChangedEvent<any>) {
    this.loadComponentsData();
  }

  private convertResults(dashboardActivityArr: DashboardActivity[]): DashboardActivity[] {
    return dashboardActivityArr.map(dashboardActivity => {
      dashboardActivity.sessionEndMethod = dashboardActivity.sessionTimeout ? "Expired" : "Logout";
      dashboardActivity.isInitialDashboardDisplay = (dashboardActivity.dashboardListOrder === "1") ? YesNoValue.YES_VALUE : YesNoValue.NO_VALUE;
      dashboardActivity = this._clientPortalActivityService.convertResult(dashboardActivity);
      return dashboardActivity;
    });
  }

  // Visualizations
  isGridGrouped(): boolean {
    const rowGroupColumns = this.gridApi?.getRowGroupColumns();
    return rowGroupColumns?.length > 0;
  }

  private loadChartDataWithoutGroup() {
    const chartData: ActivityLineChartData[] = this._clientPortalActivityService.getActivityLineChartData(this.getFilteredData(), this.scale, this._clientPortalActivityService.CHART_DATA_AMOUNT);
    this.chartOptions = this._clientPortalActivityService
      .getChartOptionsWithoutGroups(chartData,this.scale,this.chartTitle, this.page);
  }

  private loadChartDataWithGroup() {
    const groupedChartData = this._clientPortalActivityService.getGroupedActivityLineChartData(this.getGroupFilteredData(), this.scale, this._clientPortalActivityService.CHART_DATA_AMOUNT);
    this.chartOptions = this._clientPortalActivityService
      .getChartOptionsWithGroup(groupedChartData, this.scale, this.selectedDataType, this.page);
  }

  private getGroupFilteredData(): Map<string, DashboardActivity[]> {
    const groupMap: Map<string, DashboardActivity[]> = new Map();
    if (this.gridApi && !this.gridApi.isDestroyed()) {
      this.gridApi.forEachNodeAfterFilter((node: any) => {
        if (node.group && node.level === 0 && node.key) {
          const groupValue = node.key;
          groupMap.set(groupValue, []);
          this._clientPortalActivityService.collectRowsUnderGroup(node, groupMap.get(groupValue)!);
        }
      });
    }
    return groupMap;
  }
}
